import type { ChatAttachmentView } from '@/components/ChatMessageAttachments';
import type { ChatMessageView } from '@/components/ChatMessageBubble';

export type PwaChatRenderItem =
  | { type: 'message'; message: ChatMessageView }
  | {
      type: 'album';
      id: string;
      sender: string;
      images: ChatAttachmentView[];
      messageIds: string[];
    };

const EMPTY_CONTENT = new Set(['', '[Archivos adjuntos]', '[Audio]', '📎']);

function isImageAttachment(a: ChatAttachmentView): boolean {
  if (a.kind === 'image') return true;
  if (a.kind === 'video' || a.kind === 'audio' || a.kind === 'document') return false;
  return (a.mimeType || '').toLowerCase().startsWith('image/');
}

function hasNonImageAttachment(msg: ChatMessageView): boolean {
  return (msg.attachments ?? []).some((a) => !isImageAttachment(a));
}

export function imageAttachmentsOf(msg: ChatMessageView): ChatAttachmentView[] {
  return (msg.attachments ?? []).filter(isImageAttachment);
}

function usefulContent(msg: ChatMessageView): boolean {
  const t = (msg.content ?? '').trim();
  if (!t) return false;
  if (EMPTY_CONTENT.has(t)) return false;
  return true;
}

/** Message is only images (no useful text, no other media). */
export function isImageOnlyMessage(msg: ChatMessageView): boolean {
  if (msg.isWhisper) return false;
  const images = imageAttachmentsOf(msg);
  if (images.length === 0) return false;
  if (hasNonImageAttachment(msg)) return false;
  if (usefulContent(msg)) return false;
  return true;
}

/**
 * Collapse consecutive same-sender image-only messages into album items.
 * Labeled cabin messages (text + photos) stay as bubbles and break albums.
 */
export function groupConsecutiveImageMessages(messages: ChatMessageView[]): PwaChatRenderItem[] {
  const out: PwaChatRenderItem[] = [];
  let i = 0;
  while (i < messages.length) {
    const msg = messages[i]!;
    if (!isImageOnlyMessage(msg)) {
      out.push({ type: 'message', message: msg });
      i += 1;
      continue;
    }

    const sender = msg.sender;
    const images: ChatAttachmentView[] = [];
    const messageIds: string[] = [];
    while (
      i < messages.length &&
      messages[i]!.sender === sender &&
      isImageOnlyMessage(messages[i]!)
    ) {
      const cur = messages[i]!;
      images.push(...imageAttachmentsOf(cur));
      messageIds.push(cur.id);
      i += 1;
    }
    out.push({
      type: 'album',
      id: `album-${messageIds[0]}-${messageIds.length}`,
      sender,
      images,
      messageIds,
    });
  }
  return out;
}
